
import React, { useMemo, useState } from 'react'
import { Box, Typography, Checkbox, Button, useTheme } from '@mui/material'
import DescriptionRichTextComponent from './DescriptionRichTextComponent'
import DescriptionTooltipLabel from './DescriptionTooltipLabel'

interface DisplaySubtask {
  label: string
  channel?: string
  platform?: string
}
type Props = {
  subtaskOptions: Record<string, DisplaySubtask>
  selectedSubtaskKeys: string[]
  setSelectedSubtaskKeys: React.Dispatch<React.SetStateAction<string[]>> // ✅ this line fixes it
  subtaskDescriptions: Record<string, string>
  setSubtaskDescriptions: React.Dispatch<React.SetStateAction<Record<string, string>>>
  onBack: () => void
  onSubmit: () => void
  isCreating: boolean
}

const SubtaskStep: React.FC<Props> = ({
  subtaskOptions,
  selectedSubtaskKeys,
  setSelectedSubtaskKeys,
  subtaskDescriptions,
  setSubtaskDescriptions,
  onBack,
  onSubmit,
  isCreating,
}) => {
  const theme = useTheme()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [description, setDescription] = useState<string>('')
  const [editingSubtaskKey, setEditingSubtaskKey] = useState<string | null>(null)
  const [editingLabel, setEditingLabel] = useState<string>('')

  const handleOpenDescription = (key: string, label: string) => {
    setEditingSubtaskKey(key)
    setEditingLabel(label)
    setDescription(subtaskDescriptions[key] || '')
    setIsOpen(true)
  }

  const handleClose = () => {
    setDescription('')
    setIsOpen(false)
  }

  const handleSaveDescription = () => {
    if (editingSubtaskKey) {
      setSubtaskDescriptions(prev => ({
        ...prev,
        [editingSubtaskKey]: description,
      }))
    }
    setIsOpen(false)
  }

  const groupedSubtasks = useMemo(() => {
    return Object.entries(subtaskOptions).reduce((acc, [key, subtask]) => {
      const group = `${subtask.channel || 'Other'} – ${subtask.platform || 'General'}`
      if (!acc[group]) acc[group] = []
      acc[group].push({ key, ...subtask })
      return acc
    }, {} as Record<string, { key: string; label: string }[]>)
  }, [subtaskOptions])
  // console.log('subtaskOptions', subtaskOptions)
  // console.log('groupedSubtasks', groupedSubtasks)
  return (
    <Box sx={{ px: 3, pb: 3 }}>
      {/* <Typography variant='h6' sx={{ fontWeight: 'bold', mb: 2 }}>
        Select Subtasks
      </Typography> */}

      {selectedSubtaskKeys.length > 0 && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Selected Subtasks:
          </Typography>
          {Object.entries(groupedSubtasks).map(([groupLabel, tasks]) => (
            <Box key={groupLabel} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                {groupLabel}
              </Typography>

              {tasks.map(({ key, label }) => (
                // <FormControlLabel
                //   key={key}
                //   control={
                //     <Checkbox
                //       checked={selectedSubtaskKeys.includes(key)}
                //       onChange={e => {
                //         const checked = e.target.checked
                //         setSelectedSubtaskKeys(prev =>
                //           checked ? [...new Set([...prev, key])] : prev.filter(k => k !== key)
                //         )
                //       }}
                //     />
                //   }
                //   // label={label}
                //   label={
                //     <Grid
                //       container
                //       sx={{
                //         alignItems: 'center',
                //         width: '100%',
                //       }}
                //     >
                //       <Grid item xs={10}>
                //         <Typography
                //           sx={{
                //             overflow: 'hidden',
                //             textOverflow: 'ellipsis',
                //             whiteSpace: 'nowrap',
                //           }}
                //         >
                //           {label}
                //         </Typography>
                //       </Grid>

                //       <Grid item xs={2}>
                //         <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                //           <RichTextTooltip
                //             content={subtaskDescriptions[key] || 'No description'}
                //             onClick={() => handleOpenDescription(key)}
                //             isDraft={true}
                //           />
                //         </Box>
                //       </Grid>
                //     </Grid>
                //   }
                //   sx={{
                //     alignItems: 'center',
                //     width: '100%',
                //     borderRadius: 1,
                //     px: 1,
                //     py: 0.5,
                //     backgroundColor: theme.palette.background.default,
                //     border: '1px solid',
                //     borderColor: theme.palette.divider,
                //     mb: 0.5,
                //   }}
                // />
                <Box
                  key={key}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                    backgroundColor: theme.palette.background.default,
                    border: '1px solid',
                    borderColor: theme.palette.divider,
                    mb: 0.5,
                  }}
                >
                  {/* Left Side: Checkbox + Label */}
                  <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, gap: 1 }}>
                    <Checkbox
                      checked={selectedSubtaskKeys.includes(key)}
                      onChange={e => {
                        const checked = e.target.checked
                        setSelectedSubtaskKeys(prev =>
                          checked ? [...new Set([...prev, key])] : prev.filter(k => k !== key)
                        )
                      }}
                    />
                    <Typography
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {label}
                    </Typography>
                  </Box>

                  {/* Right Side: Edit icon */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <DescriptionTooltipLabel
                      content={subtaskDescriptions[key] || 'No description'}
                      onClick={() => handleOpenDescription(key, label)}
                      isDraft={true}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      )}
      <DescriptionRichTextComponent
        isOpen={isOpen}
        onClose={handleClose}
        onSave={handleSaveDescription}
        description={description}
        setDescription={setDescription}
        editingLabel={editingLabel}
      />
      {onBack && onSubmit && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button variant="outlined" onClick={onBack}>
            Back
          </Button>
          <Button variant="contained" color="secondary" onClick={onSubmit} disabled={isCreating}>
            Create Task
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default SubtaskStep
